import { useState } from "react";
import type { ChecklistItem } from "@closing/shared";
import AssigneePicker from "./AssigneePicker";
import DueDatePill from "./DueDatePill";

export default function ItemRow({
  item,
  blockedBy,
  isFirst,
  isLast,
  dropIndicator,
  onDragStart,
  onDragOver,
  onDrop,
  onToggle,
  onRename,
  onAssign,
  onDue,
  onAnswer,
  onMove,
  onDelete,
  onComments,
}: {
  item: ChecklistItem;
  /** Names of incomplete gate items — non-empty means completion is locked. */
  blockedBy?: string[];
  isFirst: boolean;
  isLast: boolean;
  dropIndicator?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onToggle: (completed: boolean) => void;
  onRename: (name: string) => void;
  onAssign: (assignee: { email: string; displayName: string } | null) => void;
  onDue: (dueOn: string | null) => void;
  onAnswer: (answer: "yes" | "no" | null) => void;
  onMove: (direction: "up" | "down") => void;
  onDelete: () => void;
  onComments: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.name);

  function commitRename() {
    setEditing(false);
    const name = draft.trim();
    if (name && name !== item.name) onRename(name);
    else setDraft(item.name);
  }

  return (
    <div
      draggable={!editing}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`group flex items-center gap-3 border-b border-line px-4 py-2 last:border-b-0 hover:bg-paper/60 ${
        dropIndicator ? "border-t-2 border-t-signature" : ""
      }`}
    >
      {/* Drag handle — pointless on touch, so desktop only. */}
      <span
        className="hidden w-3 flex-none cursor-grab select-none font-mono text-[11px] text-faint opacity-0 transition-opacity group-hover:opacity-100 active:cursor-grabbing md:block"
        title="Drag to reorder or move between sections"
        aria-hidden
      >
        ⠿
      </span>
      {(() => {
        // Gated: completion locked until the gate items are done. Unchecking
        // an already-complete item is always allowed.
        const locked = !item.completed && (blockedBy?.length ?? 0) > 0;
        return (
          <input
            type="checkbox"
            checked={item.completed}
            disabled={locked}
            onChange={(e) => onToggle(e.target.checked)}
            title={locked ? `Gated by: ${blockedBy!.join(", ")}` : undefined}
            className={`h-4 w-4 flex-none accent-[#bb2027] ${
              locked ? "cursor-not-allowed opacity-40" : "cursor-pointer"
            }`}
          />
        );
      })()}

      <div className="min-w-0 flex-1">
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") {
                setDraft(item.name);
                setEditing(false);
              }
            }}
            className="w-full border-b border-line-strong bg-transparent text-[13px] outline-none"
          />
        ) : (
          <button
            onClick={() => setEditing(true)}
            // Full name always readable on mobile (wrap); ellipsis only on
            // desktop where hover shows the title.
            className={`block w-full whitespace-normal break-words text-left text-[13px] md:truncate ${
              item.completed ? "text-faint line-through" : "text-ink"
            }`}
            title={item.description ?? item.name}
          >
            {item.name}
            {item.source === "ai" && (
              <span className="ml-2 font-mono text-[9px] uppercase tracking-wider text-signature">
                ai
              </span>
            )}
          </button>
        )}
        {/* Gate note — visible without hover so mobile users know why the
            checkbox is locked. */}
        {!item.completed && (blockedBy?.length ?? 0) > 0 && (
          <p className="mt-0.5 font-mono text-[10px] text-faint">
            🔒 gated by: {blockedBy!.join(", ")}
          </p>
        )}
      </div>

      {/* Comment thread toggle — count is denormalized on the item. */}
      <button
        onClick={onComments}
        className={`flex flex-none items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px] tabular-nums ${
          item.commentCount > 0
            ? "text-ink-soft hover:bg-line"
            : // Hover-reveal is desktop-only; stay visible on touch.
              "text-faint opacity-100 transition-opacity hover:bg-line md:opacity-0 md:group-hover:opacity-100"
        }`}
        title="Comments"
      >
        🗨{item.commentCount > 0 ? ` ${item.commentCount}` : ""}
      </button>
      {/* Yes/No answer chip — sits alongside the checkbox (Asana custom-field
          style); clicking the selected value clears it. */}
      {item.hasYesNo && (
        <span className="flex flex-none overflow-hidden rounded-full border border-line font-mono text-[10px]">
          {(["yes", "no"] as const).map((v) => (
            <button
              key={v}
              onClick={() => onAnswer(item.answer === v ? null : v)}
              className={`px-2 py-0.5 uppercase transition-colors ${
                item.answer === v
                  ? v === "yes"
                    ? "bg-[#e6f4ea] font-semibold text-positive"
                    : "bg-flag-bg font-semibold text-flag"
                  : "bg-white text-faint hover:text-ink"
              }`}
              title={`Answer ${v}`}
            >
              {v}
            </button>
          ))}
        </span>
      )}
      <DueDatePill dueOn={item.dueOn} completed={item.completed} onChange={onDue} />
      <AssigneePicker
        assigneeEmail={item.assigneeEmail}
        assigneeName={item.assigneeName}
        onChange={onAssign}
      />

      {/* Row tools — hover-revealed, desktop only (reorder/delete live in
          the full app on a computer; mobile keeps rows compact). */}
      <div className="hidden w-14 flex-none items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100 md:flex">
        <RowTool label="Move up" disabled={isFirst} onClick={() => onMove("up")}>
          ↑
        </RowTool>
        <RowTool label="Move down" disabled={isLast} onClick={() => onMove("down")}>
          ↓
        </RowTool>
        <RowTool label="Delete item" onClick={onDelete} danger>
          ×
        </RowTool>
      </div>
    </div>
  );
}

function RowTool({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={`flex h-5 w-5 items-center justify-center rounded font-mono text-[11px] disabled:opacity-20 ${
        danger ? "text-muted hover:bg-flag-bg hover:text-flag" : "text-muted hover:bg-line"
      }`}
    >
      {children}
    </button>
  );
}
